import { Campaign, Collab, activeLinks, activeUnits, isVoided } from "@foodfluence/shared";
import deliverableNames from "./deliverableNames";
import { getCollabContentRequirements, getPendingUgcAsk } from "./contentUtils";

export const isUGC = (collab: Collab): boolean => {
  // content is set once the brand accepts; pre-acceptance custom proposals carry
  // the creator's offer in proposedContent with the creatorType discriminant
  return (
    Object.keys(collab.content ?? {}).length > 0 ||
    Object.keys(collab.proposedContent ?? {}).length > 0 ||
    collab.creatorType === 'ugc'
  );
}

export const hasDeliverableLinks = (collab: Collab): boolean => {
  return collab.deliverableLinks && Object.keys(collab.deliverableLinks).length > 0;
}

/**
 * Formats deliverable string based on whether it's a content-based collab or not
 * For content-based collabs, displays based on content requirements and channels
 * For regular collabs, displays based on deliverable counts
 */
export const formatDeliverableString = (collab: Collab, campaign?: Campaign): string => {
  // Check if this is a content-based collab
  if (isUGC(collab)) {
    // A pending request or counter shows the creator's own ask, like proposedInfluencerDeliverableCounts below
    const ask = getPendingUgcAsk(collab);
    return ask ? formatUgcAsk(ask) : formatContentBasedDeliverables(collab, campaign);
  }

  if (collab.status === "pendingClientApproval" || collab.status === "influencerCountered") {
    return formatDeliverableCounts(collab.proposedInfluencerDeliverableCounts);
  }
  
  return formatDeliverableCounts(collab.deliverableCounts);
};

/**
 * Formats deliverables for content-based collabs based on content requirements and channels
 */
const formatContentBasedDeliverables = (collab: Collab, campaign?: Campaign): string => {
  const contentEntries = Object.entries(collab.content || {}).filter(([_, unit]) => !isVoided(unit));
  const resolvedRequirements = getCollabContentRequirements(collab, campaign);

  if (contentEntries.length === 0) {
    return "No content requirements";
  }

  // Check if any content units have specific social channels
  const hasSpecificChannels = contentEntries.some(([_, contentUnit]) => 
    contentUnit.channels && contentUnit.channels.length > 0
  );

  if (hasSpecificChannels) {
    // Format as social media deliverables
    const contentGroups: { [key: string]: { channels: string[], count: number } } = {};
    
    contentEntries.forEach(([contentId, contentUnit]) => {
      const channels = contentUnit.channels || [contentId];
      
      channels.forEach(channel => {
        if (!contentGroups[channel]) {
          contentGroups[channel] = { channels: [], count: 0 };
        }
        contentGroups[channel].count++;
      });
    });

    // Format the string
    const formattedParts = Object.entries(contentGroups)
      .map(([channel, { count }]) => {
        const deliverableName = deliverableNames[channel as keyof typeof deliverableNames];
        if (deliverableName) {
          return `${count}x ${deliverableName.short}`;
        }
        // Fallback for unknown channels
        return `${count}x ${channel}`;
      })
      .sort()
      .join(' ');

    return formattedParts || "Content-based deliverables";
  } else {
    // No specific channels - use campaign content requirements to determine content types
    const genericContentCounts: { [key: string]: number } = {};
    
    contentEntries.forEach(([contentId, contentUnit]) => {
      const contentType = getContentTypeFromCampaign(contentId, resolvedRequirements);
      genericContentCounts[contentType] = (genericContentCounts[contentType] || 0) + 1;
    });

    const formattedParts = Object.entries(genericContentCounts)
      .map(([type, count]) => `${count}x ${type}`)
      .sort()
      .join(' ');

    return formattedParts || "Generic content";
  }
};

/** The creator's pending UGC ask, in the same "Nx type" form as formatContentBasedDeliverables. */
const formatUgcAsk = (ask: Record<string, { type?: string; count?: number }>): string => {
  const counts = Object.entries(ask).reduce<Record<string, number>>((acc, [key, requirement]) => {
    const type = getContentTypeFromCampaign(key, ask);
    acc[type] = (acc[type] || 0) + Math.max(1, requirement.count ?? 1);
    return acc;
  }, {});
  return Object.entries(counts).map(([type, count]) => `${count}x ${type}`).sort().join(' ');
};

/**
 * Gets content type from resolved content requirements (collab snapshot or campaign template).
 */
const getContentTypeFromCampaign = (
  contentId: string,
  contentRequirements?: Record<string, { type?: string }>
): string => {
  if (!contentRequirements?.[contentId]) {
    return 'content pieces';
  }

  const requirement = contentRequirements[contentId];

  // Use the content type from the requirement
  if (requirement.type) {
    switch (requirement.type) {
      case 'short':
        return 'short content';
      case 'standard':
        return 'standard content';
      case 'long':
        return 'long content';
      case 'extended':
        return 'extended content';
      default:
        return requirement.type;
    }
  }
  
  // Fallback to content pieces if no type specified
  return 'content pieces';
};

/**
 * Formats deliverables for regular collabs based on deliverable counts
 */
export const formatDeliverableCounts = (deliverableCounts: Record<string, number>): string => {
  if (!deliverableCounts) return "No deliverables";
  
  return Object.entries(deliverableCounts)
    .filter(([_, count]) => count > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([type, count]) => {
      const deliverableName = deliverableNames[type as keyof typeof deliverableNames];
      if (deliverableName) {
        return `${count}x ${deliverableName.short}`;
      }
      // Fallback for unknown types
      return `${count}x ${type}`;
    })
    .join(' ');
};

/**
 * Whether the creator has nothing left to submit. Voided slots are excluded — the brand withdrew
 * them, so they must not hold this at `false` forever (the backend twin of this feeds the
 * `lastContentSubmitted` flag that stops reminders).
 */
export const isLastContentSubmitted = (collab: Collab, isDraftRequired: boolean): boolean => {

  // Check collab.content for UGC collabs
  if (isUGC(collab)) {
    return activeUnits(collab.content).every(content => content.status !== 'pendingSubmission');
  }

  if (!collab.deliverableLinks || Object.keys(collab.deliverableLinks).length === 0) {
    return false;
  }

  const blocking = isDraftRequired ? "pendingDraftSubmission" : "pendingSubmission";
  return !activeLinks(collab.deliverableLinks).some(link => link.status === blocking);
};

const waitingForDraftVerification = (collab: Collab): boolean => {
  if (collab.status !== 'pendingSubmission') return false;

  if (isUGC(collab)) {
    const units = activeUnits(collab.content);
    return units.length > 0 && units.every(content => content.status === 'pendingVerification')
  }

  if (hasDeliverableLinks(collab)) {
    const links = activeLinks(collab.deliverableLinks);
    // `[].every(...)` is vacuously true, so without this an all-voided collab reported "waiting for
    // the brand to verify" work that was never submitted. Nothing outstanding means nothing to wait on.
    if (links.length === 0) return false;
    if (!links.every(link => link.status === "pendingDraftReview")) {
      return false;
    }
  }

  return true;
}

const waitingForLivePostVerification = (collab: Collab): boolean => {
  if (collab.status !== 'pendingSubmission') return false;
  let result = true;

  // have to add this check because UGC collabs in submitting draft stage without deliverableLinks will return true
  if (isUGC(collab)) {
    const units = activeUnits(collab.content);
    result = units.length > 0 && units.every(content => content.status === 'pendingVerification')
  }

  if (hasDeliverableLinks(collab)) {
    const links = activeLinks(collab.deliverableLinks);
    if (links.length === 0) return false;
    if (!links.every(link => link.status === "pendingVerification")) {
      return false;
    }
  }

  return result;
}

const waitingForDraftOrLivePostVerification = (collab: Collab): boolean => {
  if (collab.status !== 'pendingSubmission') return false;

  if (isUGC(collab)) {
    const units = activeUnits(collab.content);
    if (units.length === 0) return false;
    if (!units.every(content => content.status === "pendingVerification" || content.status === 'approved')) {
      return false;
    }
  }

  if (hasDeliverableLinks(collab)) {
    const links = activeLinks(collab.deliverableLinks);
    if (links.length === 0) return false;
    if (!links.every(link => link.status === "pendingDraftReview" || link.status === "pendingVerification")) {
      return false;
    }
  }

  return true;
}

export const waitingForVerification = (collab: Collab): boolean => {
  return waitingForDraftVerification(collab) || waitingForLivePostVerification(collab) || waitingForDraftOrLivePostVerification(collab);
}